/**
 * THE definition of "is this string actually a hospital?" (issue #2126).
 *
 * One list, three consumers. It lives in `packages/contracts` because that is
 * the only module the web app AND the native app can both import — the web
 * `lib/geocode-sentinels.ts` and `apps/mobile/lib/safety/hospitalSentinels.ts`
 * both re-export from here rather than keeping mirrored copies. They WERE
 * mirrored at first, and the mirrors drifted within a day: a marker added to
 * two of the three left the web completeness lock, every project writer, every
 * render context, the site poster and the worker-facing site details still
 * accepting a value the other two rejected. The parity test caught it — which
 * is the argument for deleting the duplication rather than testing it harder.
 *
 * WHAT GETS REJECTED, and why each is not a hospital:
 *
 *  1. The nearest-A&E lookup's three misses (`lib/geocode.ts`). All non-blank,
 *     human-readable, and addressed to the app user rather than to a crew:
 *       "Could not locate address — please enter nearest A&E manually."
 *       "No A&E hospitals found within 20km — please enter nearest A&E manually."
 *       "No nearby hospital found"
 *     Pre-#2126 code persisted these into `Project.nearestHospital`, so real
 *     rows hold them.
 *
 *  2. The AI safety-draft placeholder (`lib/ai/safety-draft.ts`), which the
 *     model is instructed to write INSTEAD of guessing a hospital. Honest, and
 *     right for a reader — but it is not a hospital, and counted as filled it
 *     opens the site-safety completeness lock, so the product stops asking and
 *     a project reads as safety-complete with its A&E genuinely unknown.
 *
 * Matching is by SUBSTRING so a reworded miss still fails closed. The phrases
 * are deliberately specific: a bare "to be confirmed" would reject
 * "Leeds General Infirmary, Great George St — phone to be confirmed", which
 * names a hospital and must be accepted.
 */
import { z } from "zod";

export const NEAREST_HOSPITAL_MISS_MARKERS = [
  "could not locate address",
  "no a&e hospitals found",
  "no nearby hospital found",
  "enter nearest a&e manually",
  // The AI placeholder — enough of the phrase to be unmistakable, and not so
  // little that an ordinary pending detail gets caught by it.
  "to be confirmed before work starts",
] as const;

/** False for a blank value, a lookup miss, or the AI's decline-to-guess text. */
export function isRealNearestHospital(value: string | null | undefined): boolean {
  const normalised = value?.trim().toLowerCase() ?? "";
  if (!normalised) return false;
  return !NEAREST_HOSPITAL_MISS_MARKERS.some((marker) =>
    normalised.includes(marker),
  );
}

/**
 * The wizard field schema. Keeps the original length rule so the ordinary
 * "you left it blank" message is unchanged, and adds the refusal on top with a
 * message that tells the user what they are actually looking at — a pre-filled
 * "we couldn't find it" is not an address, and nothing else on screen says so.
 */
export function nearestHospitalField(lengthMessage: string) {
  return z
    .string()
    .min(3, lengthMessage)
    .refine(isRealNearestHospital, {
      message:
        "That's a 'not found' message, not a hospital. Enter the nearest A&E yourself.",
    });
}
